import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateConfigurationSelection from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateConfigurationSelection";
import translate from "@insite/client-framework/Translate";
import { ConfigSectionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { IconWrapper } from "@insite/mobius/Icon";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonIconPresentationProps } from "@stoneage/MobiusOverrides/Button/Button";
import { color } from "@stoneage/XDL/Utilities";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasParentProductId &
    HasProduct &
    HasHistory;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => {
    const productState = getProductState(state, ownProps.parentProductId).value;
    return {
        configurationType: productState?.configurationType,
        configuration: productState?.detail?.configuration,
        configurationSelection: state.pages.productDetails.configurationSelection,
        hideProductBundleOptions: (getSettingsCollection(state) as any).customSettings?.["hideProductBundleOptions"],
        // XNG-Change
        jettingAppPageLink: getPageLinkByPageType(state, "JettingAppPage")?.url || "JettingCalculator",
    };
};

const mapDispatchToProps = {
    updateConfigurationSelection,
};

export interface ProductDetailsBundleSectionOptionsStyles {
    wrapper?: InjectableCss;
    select?: SelectProps;

    accordionWrapper?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
    singleOptionField?: TextFieldPresentationProps;
    multiSelectLabel?: TypographyProps;
    // XNG-Change
    configureJettingWrapper?: InjectableCss;
    button?: ButtonIconPresentationProps;
}

export const variantOptionsStyles: ProductDetailsBundleSectionOptionsStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    select: {
        cssOverrides: {
            formField: css`
                margin-bottom: 12px;
                &&& select {
                    font-size: 14px;
                }
            `,
        },
    },
    accordionWrapper: {
        css: css`
            border-bottom: 1px solid ${getColor("common.ui6")};
            padding-bottom: 15px;
            margin-bottom: 15px;
        `,
    },
    accordionSection: {
        headerProps: {
            css: css`
                &&&& button {
                    padding: 0 !important;
                    span {
                        font-size: 12px;
                        line-height: 18px;
                    }
                    ${IconWrapper} svg {
                        stroke-width: 4px;
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                background: transparent;
                padding: 0;
            `,
        },
    },
    singleOptionField: {
        cssOverrides: {
            formField: css`
                margin-top: 15px;
                &&& label {
                    opacity: 1;
                }
            `,
            inputSelect: css`
                background: transparent !important;
                color: ${getColor("text.main")} !important;
                opacity: 1 !important;
                font-size: 14px !important;
                text-overflow: ellipsis;
            `,
        },
    },
    multiSelectLabel: {
        size: "12px",
        lineHeight: "18px",
        weight: "bold",
        css: css`
            display: block;
            margin-bottom: 14px;
        `,
    },
    configureJettingWrapper: {
        css: css`
            padding-bottom: 16px;
            border-bottom: 1px solid ${color("common.ui6")};
            margin-bottom: 16px;
            margin-top: 4px;
        `,
    },
    button: {
        css: css`
            width: 100%;
            justify-content: center;
        `,
    },
};

const styles = variantOptionsStyles;

const ProductDetailsBundleSectionOptions = ({
    configurationType,
    configuration,
    configurationSelection,
    updateConfigurationSelection,
    parentProductId,
    hideProductBundleOptions,
    product,
    // XNG-Change
    history,
    jettingAppPageLink,
}: Props) => {
    const [configObject, setConfigObject] = useState<Record<string, string>>({});

    let disableOptions = false;

    // XNG-Change: take initial configs from url and updateConfigurationSelection
    useEffect(() => {
        if (
            !configuration ||
            configuration.isKit ||
            !configuration.configSections ||
            configuration.configSections.length === 0 ||
            configurationType === ConfigurationType.None ||
            (hideProductBundleOptions?.toLowerCase() === "true" &&
                product.properties["hideBundleOptions"]?.toLowerCase() === "true")
        ) {
            return;
        }

        const paramsObject = getQueryParams();
        let configSelectionsNames = product.productNumber;
        let total = 0;
        if (paramsObject && Object.keys(paramsObject)) {
            setConfigObject(paramsObject);
            configuration.configSections.forEach(configSection => {
                const configValue = paramsObject[`config${configSection.sortOrder}`];
                if (configValue) {
                    const sectionOption = configSection?.sectionOptions?.find(
                        option => (option as any).erpNumber === configValue,
                    );

                    if (configSection.id && sectionOption?.id) {
                        configSelectionsNames += `, ${(sectionOption as any).erpNumber}(${
                            (sectionOption as any).quantity
                        })`;
                        total += Number((sectionOption as any).quantity);

                        updateConfigurationSelection({
                            configSectionId: configSection.id,
                            sectionOptionId: sectionOption.id,
                            productId: parentProductId,
                        });
                    }
                }
            });
        }
        const singleOptionConfigs = configuration?.configSections?.filter(
            configSection => configSection.sectionOptions && configSection.sectionOptions?.length === 1,
        );

        if (singleOptionConfigs?.length) {
            singleOptionConfigs.forEach(singleOptionConfig => {
                const sectionOption = singleOptionConfig.sectionOptions?.[0];
                if (sectionOption) {
                    configSelectionsNames += `, ${(sectionOption as any).erpNumber}(${
                        (sectionOption as any).quantity
                    })`;
                    total += Number((sectionOption as any).quantity);
                }
            });
        }
        // save partNumber with selection in localstorage
        if (configSelectionsNames) {
            //  configSelectionsNames += `Total:${total}`;
            localStorage.setItem("productPartNumber", configSelectionsNames);
        }
    }, []);

    const getQueryParams = (): Record<string, string> => {
        // Extract query string from the URL
        const url = window.location.href;
        const queryString = url.split("?")[1]?.replace(/&$/, ""); // Remove trailing '&' if present
        if (!queryString) {
            return {};
        } // Return an empty object if no query params

        return queryString.split("&").reduce((acc: Record<string, string>, param) => {
            const [key, value] = param.split("=");
            if (key) {
                acc[key] = decodeURIComponent(value || "");
            } // Handle empty values
            return acc;
        }, {});
    };

    const sectionOptionChangeHandler = (configSectionId: string, sectionOptionId: string) => {
        updateConfigurationSelection({ configSectionId, sectionOptionId, productId: parentProductId });
    };

    if (
        !configuration ||
        configuration.isKit ||
        !configuration.configSections ||
        configuration.configSections.length === 0 ||
        configurationType === ConfigurationType.None ||
        (hideProductBundleOptions?.toLowerCase() === "true" &&
            product.properties["hideBundleOptions"]?.toLowerCase() === "true")
    ) {
        return null;
    }

    const singleOptionConfigs = configuration?.configSections?.filter(
        configSection => configSection.sectionOptions && configSection.sectionOptions?.length === 1,
    );
    const multiOptionConfigs = configuration?.configSections?.filter(
        configSection => configSection.sectionOptions && configSection.sectionOptions?.length > 1,
    );

    // XNG-Change: disable dropdowns if isJettingAppRequired is true and has jettingCalculatorId
    let link;
    if (
        product.properties["isJettingAppRequired"]?.toLowerCase() === "true" &&
        product.properties["jettingCalculatorId"]
    ) {
        disableOptions = true;
    }

    const handleConfigureJetting = () => {
        link = `${jettingAppPageLink}?calculatorId=${product.properties["jettingCalculatorId"]}&Tool=${product.productNumber}&Family=${product.properties["jettingCalculatorFamily"]}&`;
        const queryString = Object.entries(configObject)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join("&");

        link += `${queryString}`;
        history.push(link);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {singleOptionConfigs && singleOptionConfigs.length > 0 && (
                <StyledWrapper {...styles.accordionWrapper}>
                    <ManagedAccordionSection
                        title={translate("View All ({0}) Included Parts", singleOptionConfigs.length.toString())}
                        initialExpanded={false}
                        {...styles.accordionSection}
                    >
                        {singleOptionConfigs.map((configSection, index) => (
                            <SingleOptionConfiguration
                                key={configSection.id}
                                configSection={configSection}
                                sectionOptionChangeHandler={sectionOptionChangeHandler}
                                itemIndex={index}
                                multiOptionsLength={multiOptionConfigs?.length}
                            />
                        ))}
                    </ManagedAccordionSection>
                </StyledWrapper>
            )}
            {multiOptionConfigs && multiOptionConfigs.length > 0 && (
                <>
                    <Typography {...styles.multiSelectLabel}>
                        {translate("Select Your ({0}) Configurable Parts", multiOptionConfigs.length.toString())}
                    </Typography>
                    {multiOptionConfigs?.map(
                        (configSection, index) =>
                            !!configSection.sectionOptions?.length && (
                                <Select
                                    disabled={disableOptions}
                                    {...styles.select}
                                    key={configSection.id}
                                    // It would have been easier to use <ol type="A"> but unfortunately
                                    // it will mess up the design very badly, so using ASCII based numbers is a way to go
                                    label={`${configSection.label || configSection.sectionName}`}
                                    value={configurationSelection[configSection.id]}
                                    onChange={event => {
                                        sectionOptionChangeHandler(configSection.id, event.currentTarget.value);
                                    }}
                                    data-test-selector={`styleSelect_${configSection.sectionName}`}
                                >
                                    <option value="">
                                        {disableOptions
                                            ? "- -"
                                            : `${translate("Select")} ${
                                                  configSection.label || configSection.sectionName
                                              }`}
                                    </option>
                                    {configSection.sectionOptions?.map(sectionOption => (
                                        <option value={`${sectionOption.id}`} key={`${sectionOption.id}`}>
                                            {`${sectionOption.description} (x${sectionOption.quantity})`}
                                        </option>
                                    ))}
                                </Select>
                            ),
                    )}
                </>
            )}
            {/* XNG-Change */}
            {disableOptions && (
                <StyledWrapper {...styles.configureJettingWrapper}>
                    <Button onClick={handleConfigureJetting} {...styles.button}>
                        {translate("Configure Jetting")}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

type SingleOptionProps = {
    configSection: ConfigSectionModel;
    sectionOptionChangeHandler: (configSectionId: string, sectionOptionId: string) => void;
    multiOptionsLength?: number;
    itemIndex: number;
};

const SingleOptionConfiguration = ({
    configSection,
    sectionOptionChangeHandler,
    multiOptionsLength,
    itemIndex,
}: SingleOptionProps) => {
    // Todo: try to enhance the performance here, we are calling real time APIs where its not necessary
    React.useEffect(() => {
        if (configSection) {
            sectionOptionChangeHandler(configSection.id, configSection!.sectionOptions![0].id);
        }
    }, [configSection]);
    return (
        <TextField
            key={configSection.id}
            value={`${configSection!.sectionOptions![0].description}`}
            label={`${configSection.label || configSection.sectionName} (x${
                configSection!.sectionOptions![0].quantity
            })`}
            disabled={true}
            {...styles.singleOptionField}
        />
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(
        withParentProductId(
            connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductDetailsBundleSectionOptions)),
        ),
    ),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
